// Full-page Doctor × Weekday slot heatmap. Reached from the left sidebar
// ("Slots"). It owns all view state, fetches its own doctor/tenant options (it
// lives outside the calendar shell), and has two data modes:
//   • "definitions" — the recurring slot templates (`useSlotDefinitions`)
//   • "available"   — live free-slot availability for the week (`useAvailableSlots`)
// Only the active mode's hook fetches; both share the matrix / detail rail.

import { useEffect, useMemo, useRef, useState } from "react";
import { DateTime } from "luxon";
import { AlertTriangle, CalendarPlus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { fetchTenants } from "@/lib/api/tenants";
import { filterClinicsForSurface } from "@/lib/clinic-visibility";
import { fetchDoctors } from "@/lib/api/users";
import { resolveTenantDisplayName } from "@/lib/tenant-display";
import { createAdminSlot, deleteAdminSlot, updateAdminSlot } from "@/lib/api/admin-slots";
import { publishCalendarRefresh } from "@/lib/realtime/appointment-realtime-bridge";
import { useHasPermission } from "@/hooks/use-permissions";
import { useHideSunday } from "@/hooks/use-user-preferences";
import { useSlotDefinitions } from "../day-view/use-slot-definitions";
import { SlotFormSheet } from "../day-view/slot-form-sheet";
import { startOfWeek } from "../calendar-utils";
import { Toolbar } from "./toolbar";
import { HeatmapMatrix } from "./heatmap-matrix";
import { DetailRail } from "./detail-rail";
import { useAvailableSlots } from "./use-available-slots";
import { BulkEditSheet, BULK_FIELD_LABELS } from "./bulk-edit-sheet";
import { DAY_SCALES, formatRange, hashHue, slotSession, slotWeekdays } from "./heatmap-utils";

// Map a raw tenants API item to the option shape `useSlotDefinitions` /
// `SlotFormSheet` expect (mirrors calendar.jsx).
function toTenantOption(item) {
  const id = String(item?.id || item?._id || "").trim();
  if (!id) return null;
  const name = resolveTenantDisplayName(item, id);
  const country = String(item?.country || "").trim().toUpperCase();
  const timezone = String(item?.timezone || "").trim();
  return { id, name, country, timezone };
}

// Map a raw doctors API item to the option shape (mirrors calendar.jsx).
function toDoctorOption(item) {
  const id = String(item?.id || item?._id || "").trim();
  if (!id) return null;
  const firstName = String(item?.firstName || "").trim();
  const lastName = String(item?.lastName || "").trim();
  const fullName = `${firstName} ${lastName}`.trim();
  const displayName = String(item?.displayName || "").trim();
  return { id, name: fullName || displayName || id };
}

// Sort month-day / month-week values so the "Last" sentinel (-1) lands at the
// end rather than the beginning (mirrors the slot-form-sheet helpers).
function sortMonthSentinelValues(values) {
  return [...(values || [])].sort((a, b) => {
    if (a === -1) return 1;
    if (b === -1) return -1;
    return a - b;
  });
}

// Fields that, when changed, force the `recurrence` block to be rebuilt and
// sent. The recurrence sub-object is replaced atomically by the PUT — there's
// no per-field merge on the server — so any one change here means we rebuild
// the whole thing, reading unchanged components off the slot itself.
const RECURRENCE_TRIGGER_KEYS = [
  "slotType",
  "frequency",
  "monthlyVariant",
  "weekdays",
  "monthDays",
  "monthWeeks",
  "repeatIntervalWeeks",
  "repeatIntervalMonths",
  // Extra one-off RDATEs added on top of the primary cadence.
  "dates",
  // EXDATEs subtracted from the primary cadence.
  "excludeDates",
  // Time changes flow into recurrence.startTime/endTime too.
  "startTime",
  "endTime",
];

// Rebuild the slot's `recurrence` object using form values for keys the user
// changed and the slot's own existing values for everything else. Falls back
// to sensible per-slot defaults (start weekday / day-of-month) when an array
// would otherwise be left empty by a missing slot value.
function buildEffectiveRecurrence(slot, values, changed) {
  const existing =
    slot?.recurrence && typeof slot.recurrence === "object" ? slot.recurrence : {};
  const effectiveFrequency = changed.frequency
    ? values.frequency
    : String(existing.frequency || "weekly").toLowerCase() === "monthly"
      ? "monthly"
      : "weekly";
  const effectiveStartTime = changed.startTime
    ? values.startTime
    : existing.startTime || slot?.startAt?.toFormat?.("HH:mm") || "09:00";
  const effectiveEndTime = changed.endTime
    ? values.endTime
    : existing.endTime || slot?.endAt?.toFormat?.("HH:mm") || "09:30";

  // Extra RDATE-style dates can ride along on either cadence. Treat changed
  // ⇒ user list (incl. empty = "clear"); untouched ⇒ keep the slot's list.
  const existingDates = Array.isArray(existing.dates)
    ? [...existing.dates].filter((d) => typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d.trim())).map((d) => d.trim())
    : [];
  const effectiveDates = changed.dates
    ? Array.from(new Set(Array.isArray(values.dates) ? values.dates : [])).sort()
    : existingDates;

  // EXDATE-style exclusions mirror the RDATE handling above.
  const existingExcludeDates = Array.isArray(existing.excludeDates)
    ? [...existing.excludeDates].filter((d) => typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d.trim())).map((d) => d.trim())
    : [];
  const effectiveExcludeDates = changed.excludeDates
    ? Array.from(new Set(Array.isArray(values.excludeDates) ? values.excludeDates : [])).sort()
    : existingExcludeDates;

  if (effectiveFrequency === "monthly") {
    const existingMonthDays = Array.isArray(existing.monthDays) ? existing.monthDays : [];
    const existingMonthWeeks = Array.isArray(existing.monthWeeks) ? existing.monthWeeks : [];
    const existingWeekdays = Array.isArray(existing.weekdays) ? existing.weekdays : [];
    const slotVariant =
      existingMonthDays.length > 0
        ? "dayOfMonth"
        : existingMonthWeeks.length > 0
          ? "nthWeekday"
          : "dayOfMonth";
    const effectiveMonthlyVariant = changed.monthlyVariant
      ? values.monthlyVariant
      : slotVariant;
    const recurrence = {
      frequency: "monthly",
      startTime: effectiveStartTime,
      endTime: effectiveEndTime,
      repeatIntervalMonths: changed.repeatIntervalMonths
        ? Math.max(1, Number(values.repeatIntervalMonths) || 1)
        : Number(existing.repeatIntervalMonths) || 1,
    };
    if (effectiveMonthlyVariant === "dayOfMonth") {
      const effectiveMonthDays = changed.monthDays ? values.monthDays : existingMonthDays;
      if (effectiveMonthDays.length > 0) {
        recurrence.monthDays = sortMonthSentinelValues(effectiveMonthDays);
      } else {
        const day = slot?.startAt?.day;
        recurrence.monthDays = [Number.isInteger(day) && day >= 1 && day <= 31 ? day : 1];
      }
    } else {
      const effectiveMonthWeeks = changed.monthWeeks
        ? values.monthWeeks
        : existingMonthWeeks;
      const effectiveWeekdays = changed.weekdays ? values.weekdays : existingWeekdays;
      recurrence.monthWeeks = sortMonthSentinelValues(effectiveMonthWeeks);
      recurrence.weekdays = [...(effectiveWeekdays || [])].sort((a, b) => a - b);
    }
    if (effectiveDates.length > 0) recurrence.dates = effectiveDates;
    if (effectiveExcludeDates.length > 0) recurrence.excludeDates = effectiveExcludeDates;
    return recurrence;
  }

  // weekly
  const existingWeekdays = Array.isArray(existing.weekdays) ? existing.weekdays : [];
  const effectiveWeekdays = changed.weekdays ? values.weekdays : existingWeekdays;
  const fallbackWeekdays = Number.isInteger(slot?.startAt?.weekday)
    ? [slot.startAt.weekday]
    : [1];
  const weekly = {
    frequency: "weekly",
    weekdays: (effectiveWeekdays.length > 0 ? effectiveWeekdays : fallbackWeekdays)
      .slice()
      .sort((a, b) => a - b),
    startTime: effectiveStartTime,
    endTime: effectiveEndTime,
    repeatIntervalWeeks: changed.repeatIntervalWeeks
      ? Math.max(1, Number(values.repeatIntervalWeeks) || 1)
      : Number(existing.repeatIntervalWeeks) || 1,
  };
  if (effectiveDates.length > 0) weekly.dates = effectiveDates;
  if (effectiveExcludeDates.length > 0) weekly.excludeDates = effectiveExcludeDates;
  return weekly;
}

// Build a PARTIAL PUT body from the bulk-editor's form values, the per-slot
// data, and the `changed` map (which fields the user actually altered). Top
// level keys are only included for fields the user touched — every untouched
// key on every slot stays exactly as the server has it. Sub-objects (`time`,
// `recurrence`, `metadata`) replace atomically, so the unchanged components
// inside them are read off the slot to preserve them.
function buildBulkUpdateBody(slot, values, changed) {
  const body = {};

  if (changed.slotType) {
    body.slotType = values.slotType === "recurring" ? "recurring" : "static";
  }

  // `time` is atomic in the PUT — if any time component changed, we send the
  // whole block, merging in untouched components from the slot itself.
  const timeChanged = changed.startTime || changed.endTime || changed.timezone;
  if (timeChanged) {
    const slotRecurrence =
      slot?.recurrence && typeof slot.recurrence === "object" ? slot.recurrence : {};
    const tz = changed.timezone
      ? String(values.timezone || "UTC").trim() || "UTC"
      : String(slot?.timezone || slotRecurrence.timezone || "UTC").trim() || "UTC";
    const startTime = changed.startTime
      ? values.startTime
      : slotRecurrence.startTime || slot?.startAt?.toFormat?.("HH:mm") || "09:00";
    const endTime = changed.endTime
      ? values.endTime
      : slotRecurrence.endTime || slot?.endAt?.toFormat?.("HH:mm") || "09:30";
    const anchorYmd =
      slot?.startAt?.toISODate?.() ||
      slot?.startAt?.toFormat?.("yyyy-LL-dd") ||
      DateTime.now().toISODate();
    const startDt = DateTime.fromISO(`${anchorYmd}T${startTime}`, { zone: tz });
    const endDt = DateTime.fromISO(`${anchorYmd}T${endTime}`, { zone: tz });
    body.time = {
      start: startDt.toUTC().toISO(),
      end: endDt.toUTC().toISO(),
      timezone: tz,
    };
  }

  // `recurrence` also replaces atomically — rebuild it whenever any
  // recurrence-shape OR time field changed.
  const recurrenceChanged = RECURRENCE_TRIGGER_KEYS.some((key) => changed[key]);
  if (recurrenceChanged) {
    const effectiveSlotType = changed.slotType
      ? values.slotType
      : slot?.slotType === "recurring"
        ? "recurring"
        : "static";
    body.recurrence =
      effectiveSlotType === "recurring"
        ? buildEffectiveRecurrence(slot, values, changed)
        : null;
  }

  if (changed.bookingMode) {
    body.bookingMode = values.bookingMode === "manual" ? "manual" : "auto";
  }

  if (changed.subType) {
    body.subType = String(values.subType || "");
  }

  if (changed.placeholder) {
    body.placeholder = String(values.placeholder || "").trim();
  }

  // `metadata` is atomic on the server — when any metadata-bound field is
  // touched we rebuild the whole map from the slot's baseline, overwriting
  // only the touched keys. _moreTests is gated on the effective subType so
  // switching a slot away from followUp also clears the flag.
  const metadataChanged = changed.colorHex || changed.moreTests || changed.subType;
  if (metadataChanged) {
    const baseMetadata =
      slot?.metadata && typeof slot.metadata === "object" ? slot.metadata : {};
    const nextMetadata = { ...baseMetadata };

    if (changed.colorHex) {
      const colorHex = String(values.colorHex || "").trim().toLowerCase();
      if (colorHex && /^#[0-9a-f]{6}$/.test(colorHex)) {
        nextMetadata.colorHex = colorHex;
      } else {
        delete nextMetadata.colorHex;
      }
    }

    const effectiveSubType = changed.subType
      ? String(values.subType || "")
      : String(slot?.subType || "");
    if (effectiveSubType === "followUp") {
      // Pick the value the user actually edited; otherwise keep what's on
      // the slot already so switching ONLY the subType to followUp doesn't
      // accidentally turn off a flag set elsewhere.
      const moreTests = changed.moreTests
        ? values.moreTests === true
        : nextMetadata._moreTests === true;
      if (moreTests) {
        nextMetadata._moreTests = true;
      } else {
        delete nextMetadata._moreTests;
      }
    } else {
      delete nextMetadata._moreTests;
    }

    body.metadata = nextMetadata;
  }

  return body;
}

// Build a FULL POST body for `createAdminSlot` from a source slot plus the
// bulk-editor's overrides. Unlike `buildBulkUpdateBody` (which only emits
// touched fields), this always emits every required slot field — touched
// fields use the form values, untouched fields are cloned from the source —
// because the server is making a new document, not patching an old one.
// Identity bits (`tenantId` / `doctorId` / `calendarId` / anchor date / ids
// the server generates) are always read off the source: the bulk-editor
// doesn't expose them and they're inherently per-slot.
function buildBulkCreateBody(slot, values, changed) {
  const slotRecurrence =
    slot?.recurrence && typeof slot.recurrence === "object" ? slot.recurrence : {};

  const effectiveSlotType = changed.slotType
    ? values.slotType === "recurring"
      ? "recurring"
      : "static"
    : slot?.slotType === "recurring"
      ? "recurring"
      : "static";

  const effectiveBookingMode = changed.bookingMode
    ? values.bookingMode === "manual"
      ? "manual"
      : "auto"
    : String(slot?.bookingMode || "auto") === "manual"
      ? "manual"
      : "auto";

  const tz = changed.timezone
    ? String(values.timezone || "UTC").trim() || "UTC"
    : String(slot?.timezone || slotRecurrence.timezone || "UTC").trim() || "UTC";

  const startTime = changed.startTime
    ? values.startTime
    : slotRecurrence.startTime || slot?.startAt?.toFormat?.("HH:mm") || "09:00";
  const endTime = changed.endTime
    ? values.endTime
    : slotRecurrence.endTime || slot?.endAt?.toFormat?.("HH:mm") || "09:30";

  // Duplicate + static: honor a user-picked specific date for the copies;
  // otherwise fall back to the source slot's own anchor date so each copy
  // lands on its original day. Only applied to static copies — a recurring
  // slot's anchor drives its cycle offset and isn't a user-facing date.
  const overrideYmd =
    effectiveSlotType === "static" &&
    changed.date &&
    typeof values.date === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(values.date)
      ? values.date
      : "";
  const anchorYmd =
    overrideYmd ||
    slot?.startAt?.toISODate?.() ||
    slot?.startAt?.toFormat?.("yyyy-LL-dd") ||
    DateTime.now().toISODate();
  const startDt = DateTime.fromISO(`${anchorYmd}T${startTime}`, { zone: tz });
  const endDt = DateTime.fromISO(`${anchorYmd}T${endTime}`, { zone: tz });

  const effectiveSubType = changed.subType
    ? String(values.subType || "")
    : String(slot?.subType || "");

  // Identity overrides surfaced by the duplicate sheet:
  //   • tenantId — re-target the copy to a different clinic.
  //   • doctorId — re-target the copy to a different doctor.
  //   • calendarId — required when doctorId is overridden because calendars
  //     are doctor-scoped; can also be picked independently.
  // Each falls back to the source slot's value when not overridden.
  const effectiveTenantId = changed.tenantId
    ? String(values.tenantId || "").trim() || String(slot?.tenantId || "")
    : String(slot?.tenantId || "");
  const effectiveDoctorId = changed.doctorId
    ? String(values.doctorId || "").trim() || String(slot?.doctorId || "")
    : String(slot?.doctorId || "");
  // Honor an explicit calendar override; otherwise, if the doctor was
  // overridden the calendar must come from the sheet (the source calendar
  // belongs to the original doctor and would be rejected by the server).
  // The sheet's `validate()` already blocks submit when doctor changed
  // without a calendar pick, so this branch should never produce an empty
  // calendarId in practice.
  const effectiveCalendarId = changed.calendarId
    ? String(values.calendarId || "").trim() || String(slot?.calendarId || "")
    : changed.doctorId
      ? String(values.calendarId || "").trim()
      : String(slot?.calendarId || "");

  const body = {
    tenantId: effectiveTenantId,
    doctorId: effectiveDoctorId,
    calendarId: effectiveCalendarId,
    slotType: effectiveSlotType,
    subType: effectiveSubType,
    bookingMode: effectiveBookingMode,
    time: {
      start: startDt.toUTC().toISO(),
      end: endDt.toUTC().toISO(),
      timezone: tz,
    },
  };

  const effectivePlaceholder = changed.placeholder
    ? String(values.placeholder || "").trim()
    : String(slot?.placeholder || "").trim();
  if (effectivePlaceholder) body.placeholder = effectivePlaceholder;

  // Clone the source's metadata, then apply colorHex and _moreTests overrides.
  const baseMetadata =
    slot?.metadata && typeof slot.metadata === "object" ? slot.metadata : {};
  const nextMetadata = { ...baseMetadata };
  if (changed.colorHex) {
    const colorHex = String(values.colorHex || "").trim().toLowerCase();
    if (colorHex && /^#[0-9a-f]{6}$/.test(colorHex)) {
      nextMetadata.colorHex = colorHex;
    } else {
      delete nextMetadata.colorHex;
    }
  }
  // _moreTests is gated on the effective subType — flip to off (delete the
  // key) whenever the duplicate isn't a follow-up, so a clone of a follow-up
  // slot with a "more tests" flag doesn't carry it into a first-visit copy.
  if (effectiveSubType === "followUp") {
    const moreTests = changed.moreTests
      ? values.moreTests === true
      : nextMetadata._moreTests === true;
    if (moreTests) {
      nextMetadata._moreTests = true;
    } else {
      delete nextMetadata._moreTests;
    }
  } else {
    delete nextMetadata._moreTests;
  }
  if (Object.keys(nextMetadata).length > 0) body.metadata = nextMetadata;

  if (effectiveSlotType === "recurring") {
    const recurrenceShapeChanged = RECURRENCE_TRIGGER_KEYS.some((key) => changed[key]);
    if (recurrenceShapeChanged) {
      body.recurrence = buildEffectiveRecurrence(slot, values, changed);
    } else {
      // No edits to the recurrence — clone the source recurrence verbatim so
      // explicit-dates / extra-date metadata that `buildEffectiveRecurrence`
      // doesn't model is preserved.
      const cloned = { ...slotRecurrence };
      if (Array.isArray(slotRecurrence.weekdays)) {
        cloned.weekdays = [...slotRecurrence.weekdays];
      }
      if (Array.isArray(slotRecurrence.monthDays)) {
        cloned.monthDays = [...slotRecurrence.monthDays];
      }
      if (Array.isArray(slotRecurrence.monthWeeks)) {
        cloned.monthWeeks = [...slotRecurrence.monthWeeks];
      }
      if (Array.isArray(slotRecurrence.dates)) {
        cloned.dates = [...slotRecurrence.dates];
      }
      body.recurrence = cloned;
    }
    // Preserve the source's explicit `dates` list when the user did NOT touch
    // the dates field. If they touched it (including clearing it to empty),
    // `buildEffectiveRecurrence` already produced the right output and we
    // must not second-guess them here.
    if (
      !changed.dates &&
      !Array.isArray(body.recurrence.dates) &&
      Array.isArray(slotRecurrence.dates) &&
      slotRecurrence.dates.length > 0
    ) {
      body.recurrence.dates = [...slotRecurrence.dates];
    }
  }

  return body;
}

// Functional identity of a slot: two slots with the same fingerprint book
// identically — same people, same calendar, same time, same recurrence, same
// booking knobs — so deleting one leaves the other to cover every scenario it
// did. Used by the bulk-delete dialog to count and remove redundant copies.
// Presentation-only fields (placeholder text, colorHex) are intentionally NOT
// part of the fingerprint — they don't affect bookability.
function slotFingerprint(slot) {
  const slotType = slot?.slotType === "recurring" ? "recurring" : "static";
  const startTime =
    slot?.recurrence?.startTime || slot?.startAt?.toFormat?.("HH:mm") || "";
  const endTime =
    slot?.recurrence?.endTime || slot?.endAt?.toFormat?.("HH:mm") || "";
  const tz = String(slot?.timezone || slot?.recurrence?.timezone || "UTC");
  const bookingMode = String(slot?.bookingMode || "auto");
  const subType = String(slot?.subType || "");
  const tenantId = String(slot?.tenantId || "");
  const doctorId = String(slot?.doctorId || "");
  const calendarId = String(slot?.calendarId || "");
  let recPart = "";
  if (slotType === "recurring") {
    const r = slot?.recurrence && typeof slot.recurrence === "object" ? slot.recurrence : {};
    const frequency = String(r.frequency || "weekly").toLowerCase();
    const sortedJoin = (arr) =>
      Array.isArray(arr) ? [...arr].sort((a, b) => (a > b ? 1 : a < b ? -1 : 0)).join(",") : "";
    const weekdays = sortedJoin(r.weekdays);
    const monthDays = sortedJoin(r.monthDays);
    const monthWeeks = sortedJoin(r.monthWeeks);
    const dates = sortedJoin(r.dates);
    const intervalW = Number(r.repeatIntervalWeeks) || 1;
    const intervalM = Number(r.repeatIntervalMonths) || 1;
    recPart = `${frequency}|${weekdays}|${monthDays}|${monthWeeks}|${dates}|${intervalW}|${intervalM}`;
  } else {
    // Static slots tie to a specific calendar date, so two statics at the same
    // time on different days are NOT duplicates.
    const anchorDate = slot?.startAt?.toISODate?.() || "";
    recPart = `static|${anchorDate}`;
  }
  return [
    tenantId,
    doctorId,
    calendarId,
    slotType,
    startTime,
    endTime,
    tz,
    bookingMode,
    subType,
    recPart,
  ].join("||");
}

// Return the slots that duplicate an earlier slot in `slots` (the first of
// each fingerprint group is kept, every subsequent match is a duplicate). The
// returned list is exactly what gets deleted in "duplicates only" mode.
function findDuplicateSlots(slots) {
  const seen = new Set();
  const dupes = [];
  for (const slot of slots) {
    const fp = slotFingerprint(slot);
    if (seen.has(fp)) {
      dupes.push(slot);
    } else {
      seen.add(fp);
    }
  }
  return dupes;
}

function SlotsHeatmapPage() {
  const canManageScheduling = useHasPermission("scheduling", "manage");
  // Visible week, navigable with the toolbar. The first day honors the app's
  // WEEK_STARTS_ON setting via the shared `startOfWeek` helper (Luxon's own
  // `startOf("week")` is hardcoded to ISO Monday, which would ignore the config).
  const [weekStart, setWeekStart] = useState(() => startOfWeek(DateTime.now()));
  const fromDate = useMemo(() => weekStart.startOf("day"), [weekStart]);
  const toDate = useMemo(() => weekStart.plus({ days: 6 }).endOf("day"), [weekStart]);
  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, idx) => weekStart.plus({ days: idx })),
    [weekStart],
  );
  const rangeLabel = `${weekStart.toFormat("d LLL")} → ${weekStart
    .plus({ days: 6 })
    .toFormat("d LLL")}`;
  const isCurrentWeek =
    weekStart.toISODate() === startOfWeek(DateTime.now()).toISODate();

  // Data mode: "definitions" (recurring templates) or "available" (live free
  // slots for the week). Toggled from the toolbar.
  const [viewMode, setViewMode] = useState("definitions");

  // Primary grouping dimension: "doctor" (default) or "tenant". The matrix
  // renders one block per group with the other dimension as nested lanes.
  const [groupBy, setGroupBy] = useState("doctor");

  // Cell time axis: "work" (7am–9pm) or "full" (24h), toggled from the matrix's
  // hour-ruler legend.
  const [scaleKey, setScaleKey] = useState("work");
  const scale = DAY_SCALES[scaleKey];

  // Local preference: drop the Sunday column from the weekday grid.
  const hideSunday = useHideSunday();

  const [activeCell, setActiveCell] = useState(null); // { groupKey, weekday } | null
  const [selectedTenantIds, setSelectedTenantIds] = useState([]);
  const [selectedDoctorIds, setSelectedDoctorIds] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [slotForm, setSlotForm] = useState({
    open: false,
    mode: "create",
    slot: null,
    defaultDoctorId: "",
    defaultTenantId: "",
    selectedDate: null,
  });
  const [deleteTarget, setDeleteTarget] = useState(null); // slot row pending deletion
  const [isDeleting, setIsDeleting] = useState(false);
  const [isApplyingColor, setIsApplyingColor] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [isBulkEditing, setIsBulkEditing] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  // Which subset of `activeSlots` the bulk-delete dialog targets.
  //   • "all"        — every slot visible in the rail (legacy behavior)
  //   • "duplicates" — only slots that repeat an earlier slot's fingerprint;
  //                    one copy of each unique slot is left behind
  const [bulkDeleteScope, setBulkDeleteScope] = useState("all");
  const [bulkDuplicateOpen, setBulkDuplicateOpen] = useState(false);
  const [isBulkDuplicating, setIsBulkDuplicating] = useState(false);
  // Slot ids that were just bulk-updated — the rail uses this to flash a
  // "Just updated" indicator on each card for a few seconds.
  const [recentlyEditedSlotIds, setRecentlyEditedSlotIds] = useState(() => new Set());
  // The fields that the most recent bulk edit changed — surfaced as a small
  // badge on the rail's "Inspecting" header until the highlight expires.
  const [recentlyEditedFields, setRecentlyEditedFields] = useState([]);

  const [doctorOptions, setDoctorOptions] = useState([]);
  const [tenantOptions, setTenantOptions] = useState([]);
  const [optionsError, setOptionsError] = useState(null);
  const didInitTenantFiltersRef = useRef(false);
  const didInitDoctorFiltersRef = useRef(false);

  // Standalone page → fetch its own doctor/tenant options.
  useEffect(() => {
    let isMounted = true;
    Promise.all([fetchTenants(), fetchDoctors()])
      .then(([tenantResponse, doctorResponse]) => {
        if (!isMounted) return;
        // Clinics hidden on the "calendar" surface (Settings → Clinic visibility) stay out.
        const tenants = filterClinicsForSurface(tenantResponse?.items, "calendar")
          .map(toTenantOption)
          .filter(Boolean);
        const doctors = (Array.isArray(doctorResponse?.items) ? doctorResponse.items : [])
          .map(toDoctorOption)
          .filter(Boolean);
        setTenantOptions(tenants);
        setDoctorOptions(doctors);
        if (!didInitTenantFiltersRef.current) {
          setSelectedTenantIds(tenants.map((opt) => String(opt.id || "").trim()).filter(Boolean));
          didInitTenantFiltersRef.current = true;
        }
        if (!didInitDoctorFiltersRef.current) {
          setSelectedDoctorIds(doctors.map((opt) => String(opt.id || "").trim()).filter(Boolean));
          didInitDoctorFiltersRef.current = true;
        }
        setOptionsError(null);
      })
      .catch((error) => {
        if (!isMounted) return;
        setTenantOptions([]);
        setDoctorOptions([]);
        setOptionsError(error);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const availableTenantIds = tenantOptions
      .map((opt) => String(opt.id || "").trim())
      .filter(Boolean);
    if (!availableTenantIds.length) {
      setSelectedTenantIds([]);
      return;
    }
    setSelectedTenantIds((prev) => {
      if (!prev.length) return [];
      const kept = prev
        .map((id) => String(id || "").trim())
        .filter((id) => availableTenantIds.includes(id));
      return kept.length ? kept : availableTenantIds;
    });
  }, [tenantOptions]);

  useEffect(() => {
    const availableDoctorIds = doctorOptions
      .map((opt) => String(opt.id || "").trim())
      .filter(Boolean);
    if (!availableDoctorIds.length) {
      setSelectedDoctorIds([]);
      return;
    }
    setSelectedDoctorIds((prev) => {
      if (!prev.length) return [];
      const kept = prev
        .map((id) => String(id || "").trim())
        .filter((id) => availableDoctorIds.includes(id));
      return kept.length ? kept : availableDoctorIds;
    });
  }, [doctorOptions]);

  // Both hooks are always mounted (hook rules), but only the active mode's hook
  // actually fetches — `enabled` gates the network work.
  const definitionsData = useSlotDefinitions({
    fromDate,
    toDate,
    search: "",
    groupBy,
    sortBy: "time",
    refreshKey,
    doctorOptions,
    tenantOptions,
    enabled: viewMode === "definitions",
  });
  const availableData = useAvailableSlots({
    fromDate,
    toDate,
    search: "",
    groupBy,
    refreshKey,
    doctorOptions,
    tenantOptions,
    enabled: viewMode === "available",
  });
  const { groups, isLoading } =
    viewMode === "available" ? availableData : definitionsData;

  const selectedTenantIdSet = useMemo(() => new Set(selectedTenantIds), [selectedTenantIds]);
  const selectedDoctorIdSet = useMemo(() => new Set(selectedDoctorIds), [selectedDoctorIds]);

  // Apply tenant + doctor filters to each group's rows. Groups stay listed
  // even when a filter empties their rows (the matrix shows blank lanes).
  const matrixGroups = useMemo(() => {
    const tenantFilterEnabled = tenantOptions.length > 0;
    const doctorFilterEnabled = doctorOptions.length > 0;
    return groups.map((group) => ({
      ...group,
      rows: group.rows.filter((row) => {
        if (tenantFilterEnabled && !selectedTenantIdSet.has(String(row.tenantId || ""))) {
          const tenantId = String(row.tenantId || "").trim();
          if (!selectedTenantIdSet.has(tenantId)) {
            return false;
          }
        }
        if (doctorFilterEnabled) {
          const doctorId = String(row.doctorId || "").trim();
          if (!selectedDoctorIdSet.has(doctorId)) {
            return false;
          }
        }
        return true;
      }),
    }));
  }, [
    groups,
    tenantOptions.length,
    doctorOptions.length,
    selectedTenantIdSet,
    selectedDoctorIdSet,
  ]);

  // Group entity + slots backing the detail rail (derived from the unfiltered
  // groups so the rail still resolves the entity's name). The entity is a
  // doctor when grouped by doctor, a tenant when grouped by tenant.
  const activeGroup = useMemo(() => {
    if (!activeCell) return null;
    const group = groups.find((entry) => entry.key === activeCell.groupKey);
    return group ? { id: group.key, name: group.label } : null;
  }, [groups, activeCell]);

  const activeSlots = useMemo(() => {
    if (!activeCell) return [];
    const group = groups.find((entry) => entry.key === activeCell.groupKey);
    if (!group) return [];
    return group.rows.filter(
      (row) =>
        slotWeekdays(row).includes(activeCell.weekday) &&
        (tenantOptions.length === 0 ||
          selectedTenantIdSet.has(String(row.tenantId || "").trim())) &&
        (doctorOptions.length === 0 ||
          selectedDoctorIdSet.has(String(row.doctorId || "").trim())),
    );
  }, [
    groups,
    activeCell,
    tenantOptions.length,
    doctorOptions.length,
    selectedTenantIdSet,
    selectedDoctorIdSet,
  ]);

  const toggleTenantFilter = (tenantId) => {
    const id = String(tenantId || "").trim();
    if (!id) return;
    setSelectedTenantIds((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id],
    );
  };

  const toggleDoctorFilter = (doctorId) => {
    const id = String(doctorId || "").trim();
    if (!id) return;
    setSelectedDoctorIds((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id],
    );
  };

  const selectAllTenants = () => {
    setSelectedTenantIds(
      tenantOptions.map((opt) => String(opt.id || "").trim()).filter(Boolean),
    );
  };

  const selectAllDoctors = () => {
    setSelectedDoctorIds(
      doctorOptions.map((opt) => String(opt.id || "").trim()).filter(Boolean),
    );
  };

  const clearTenants = () => setSelectedTenantIds([]);

  const clearDoctors = () => setSelectedDoctorIds([]);

  // Stable per-slot key used everywhere selection is checked. Prefer the
  // definitionId, then mongoSlotId, then id — same fallback chain the rail
  // already uses for React keys and recently-edited highlighting.
  const slotSelectionKey = (slot) =>
    String(slot?.definitionId || slot?.mongoSlotId || slot?.id || "");

  // Per-slot selection for the rail's bulk actions. Defaults to "all visible
  // slots selected" so the existing UX (act on everything) is unchanged for
  // users who never touch a checkbox. Whenever the visible slot set changes
  // (different cell, refreshed data, filter changes) the selection is
  // re-seeded to all-selected — the previous selection wouldn't reliably map
  // onto the new slot ids anyway.
  const [selectedSlotIds, setSelectedSlotIds] = useState(() => new Set());

  // Signature of the visible slot ids — changes when the rail's slot set
  // changes by membership (not by content). Used as the only dependency for
  // the re-seed effect so background re-renders that produce the same id set
  // don't clobber the user's in-flight selection.
  const activeSlotsIdsSignature = useMemo(
    () =>
      activeSlots
        .map(slotSelectionKey)
        .filter(Boolean)
        .join("|"),
    [activeSlots],
  );

  useEffect(() => {
    setSelectedSlotIds(
      new Set(activeSlots.map(slotSelectionKey).filter(Boolean)),
    );
    // Only re-seed when the slot id membership shifts — `activeSlots` itself
    // is a fresh array every render even when the contents are the same.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlotsIdsSignature]);

  // The slot subset every bulk action actually targets. Derived (not stored)
  // so we never get out of sync with the underlying activeSlots list.
  const selectedSlots = useMemo(
    () =>
      activeSlots.filter((slot) => selectedSlotIds.has(slotSelectionKey(slot))),
    [activeSlots, selectedSlotIds],
  );

  const toggleSlotSelected = (slot) => {
    const key = slotSelectionKey(slot);
    if (!key) return;
    setSelectedSlotIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const setAllSlotsSelected = (selectAll) => {
    if (selectAll) {
      setSelectedSlotIds(
        new Set(activeSlots.map(slotSelectionKey).filter(Boolean)),
      );
    } else {
      setSelectedSlotIds(new Set());
    }
  };

  // Quick-select a whole session — AM (before 13:00) or PM (13:00 and later) —
  // by its slots' start time. Replaces the selection with exactly that half so
  // the bulk color/edit/duplicate/delete actions can target one session at once.
  const setSlotsSelectedBySession = (session) => {
    setSelectedSlotIds(
      new Set(
        activeSlots
          .filter((slot) => slotSession(slot) === session)
          .map(slotSelectionKey)
          .filter(Boolean),
      ),
    );
  };

  // Redundant copies WITHIN the user's selection — the subset they can wipe
  // when "duplicates only" is chosen in the bulk-delete dialog. Computed
  // against the selection (not all activeSlots) so the count the user sees
  // matches what actually happens.
  const duplicateSlots = useMemo(
    () => findDuplicateSlots(selectedSlots),
    [selectedSlots],
  );
  const duplicateCount = duplicateSlots.length;
  // The slot subset that will actually be deleted given the current scope.
  const bulkDeleteTargets =
    bulkDeleteScope === "duplicates" ? duplicateSlots : selectedSlots;
  const bulkDeleteCount = bulkDeleteTargets.length;

  // Normalize any date to the start of its week (per the configured first day)
  // and make it the visible week.
  const goToWeek = (date) => setWeekStart(startOfWeek(date));

  const handleNewSlot = () =>
    setSlotForm({
      open: true,
      mode: "create",
      slot: null,
      defaultDoctorId: "",
      defaultTenantId: "",
      selectedDate: DateTime.now(),
    });

  const handleAddSlotToDay = () => {
    if (!activeCell) return;
    // Seed whichever dimension is the active group — the OTHER field stays
    // empty so the user picks it in the sheet.
    const defaultDoctorId = groupBy === "doctor" ? activeCell.groupKey : "";
    const defaultTenantId = groupBy === "tenant" ? activeCell.groupKey : "";
    setSlotForm({
      open: true,
      mode: "create",
      slot: null,
      defaultDoctorId,
      defaultTenantId,
      // No `defaultWeekday` prop on SlotFormSheet — seed it with a real date in
      // the visible week that lands on the active weekday; the user picks the
      // recurrence weekdays from there. Match by ISO weekday (column order is
      // first-day-of-week dependent, so a positional index would be wrong).
      selectedDate:
        weekDates.find((d) => d.weekday === activeCell.weekday) ?? DateTime.now(),
    });
  };

  // Double-clicking a slot in the detail rail opens the editor for it.
  const handleEditSlot = (slot) =>
    setSlotForm({
      open: true,
      mode: "edit",
      slot,
      defaultDoctorId: "",
      defaultTenantId: "",
      selectedDate: null,
    });

  // Clicking the duplicate icon opens the same sheet pre-filled with the
  // source slot, but in "duplicate" mode so saving creates a new definition
  // instead of patching the original.
  const handleDuplicateSlot = (slot) =>
    setSlotForm({
      open: true,
      mode: "duplicate",
      slot,
      defaultDoctorId: "",
      defaultTenantId: "",
      selectedDate: null,
    });

  const confirmDelete = async () => {
    if (!deleteTarget || isDeleting) return;
    const slotId = String(
      deleteTarget.mongoSlotId || deleteTarget.definitionId || deleteTarget.id || "",
    ).trim();
    if (!slotId) {
      toast.error("Missing slot id.");
      return;
    }
    setIsDeleting(true);
    try {
      await deleteAdminSlot({ slotId });
      toast.success("Slot deleted.");
      setDeleteTarget(null);
      setRefreshKey((value) => value + 1);
      publishCalendarRefresh({ source: "slot-delete" });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to delete slot.");
    } finally {
      setIsDeleting(false);
    }
  };

  // Bulk-apply (or reset) the color of every SELECTED slot on the active day.
  // Other metadata keys are preserved; passing null clears `colorHex`.
  const handleApplyColor = async (colorHex) => {
    if (!selectedSlots.length || isApplyingColor) return;
    setIsApplyingColor(true);
    try {
      await Promise.all(
        selectedSlots
          .map((slot) => {
            const slotId = String(slot.mongoSlotId || slot.definitionId || "").trim();
            if (!slotId) return null;
            // The slot-definitions endpoint returns each row's full metadata
            // inline, so preserve any non-color keys when applying the color.
            const baseMetadata =
              slot.metadata && typeof slot.metadata === "object" ? slot.metadata : {};
            const nextMetadata = { ...baseMetadata };
            if (colorHex) nextMetadata.colorHex = colorHex;
            else delete nextMetadata.colorHex;
            return updateAdminSlot({ slotId, body: { metadata: nextMetadata } });
          })
          .filter(Boolean),
      );
      const count = selectedSlots.length;
      toast.success(
        colorHex
          ? `Color applied to ${count} ${count === 1 ? "slot" : "slots"}.`
          : `Color reset on ${count} ${count === 1 ? "slot" : "slots"}.`,
      );
      setRefreshKey((value) => value + 1);
      publishCalendarRefresh({ source: "slot-bulk-color" });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update slot color.");
    } finally {
      setIsApplyingColor(false);
    }
  };

  // Bulk-apply only the fields the user touched in the `BulkEditSheet`.
  // `changed` is the field-by-field map from the sheet; `buildBulkUpdateBody`
  // produces a partial PUT per slot — every field the user didn't change is
  // omitted from the body and stays as-is on each slot. After save, the
  // affected slot ids and field labels are surfaced to the rail for a
  // brief post-save highlight.
  const handleBulkEdit = async (values, changed) => {
    if (!selectedSlots.length || isBulkEditing) return;
    const changedKeys = Object.keys(changed || {}).filter((key) => changed[key]);
    if (changedKeys.length === 0) {
      toast.info("No changes to apply.");
      return;
    }
    setIsBulkEditing(true);
    try {
      const affected = [];
      const tasks = selectedSlots
        .map((slot) => {
          const slotId = String(
            slot.mongoSlotId || slot.definitionId || "",
          ).trim();
          if (!slotId) return null;
          const body = buildBulkUpdateBody(slot, values, changed);
          if (Object.keys(body).length === 0) return null;
          affected.push(slot.definitionId || slot.mongoSlotId || slot.id);
          return updateAdminSlot({ slotId, body });
        })
        .filter(Boolean);
      if (tasks.length === 0) {
        toast.info("Nothing to update.");
        return;
      }
      await Promise.all(tasks);
      const count = tasks.length;
      const labels = changedKeys
        .map((key) => BULK_FIELD_LABELS[key] || key)
        .filter(Boolean);
      const labelText = labels.length > 0 ? labels.join(", ") : "settings";
      toast.success(
        `Updated ${labelText} on ${count} ${count === 1 ? "slot" : "slots"}.`,
      );
      setBulkEditOpen(false);
      // Flag the updated slots for the rail's post-save highlight. The set is
      // cleared after a short window; the refresh below also clears it
      // implicitly when the underlying data re-arrives.
      setRecentlyEditedSlotIds(new Set(affected.filter(Boolean)));
      setRecentlyEditedFields(labels);
      window.setTimeout(() => {
        setRecentlyEditedSlotIds(new Set());
        setRecentlyEditedFields([]);
      }, 6000);
      setRefreshKey((value) => value + 1);
      publishCalendarRefresh({ source: "slot-bulk-edit" });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to apply bulk edit.",
      );
    } finally {
      setIsBulkEditing(false);
    }
  };

  // Bulk-duplicate the rail's visible slots: each source slot becomes a new
  // record via `createAdminSlot`, with any fields the user touched in the
  // sheet overridden on every copy and untouched fields cloned from the
  // source. With zero edits the copies are exact clones.
  const handleBulkDuplicate = async (values, changed) => {
    if (!selectedSlots.length || isBulkDuplicating) return;
    setIsBulkDuplicating(true);
    try {
      const tasks = selectedSlots
        .map((slot) => {
          const body = buildBulkCreateBody(slot, values, changed || {});
          if (!body.tenantId || !body.doctorId || !body.calendarId) return null;
          return createAdminSlot({ body });
        })
        .filter(Boolean);
      if (tasks.length === 0) {
        toast.info("Nothing to duplicate.");
        return;
      }
      await Promise.all(tasks);
      const count = tasks.length;
      toast.success(`Duplicated ${count} ${count === 1 ? "slot" : "slots"}.`);
      setBulkDuplicateOpen(false);
      setRefreshKey((value) => value + 1);
      publishCalendarRefresh({ source: "slot-bulk-duplicate" });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to duplicate slots.",
      );
    } finally {
      setIsBulkDuplicating(false);
    }
  };

  // Bulk-delete the slots currently selected by the dialog's scope: either
  // every slot in the rail ("all") or only duplicates of slots already kept
  // ("duplicates"). Runs the per-slot deletes in parallel and surfaces the
  // count in a toast — failures are reported but don't roll back the others.
  const handleBulkDelete = async () => {
    if (!bulkDeleteTargets.length || isBulkDeleting) return;
    const scope = bulkDeleteScope;
    setIsBulkDeleting(true);
    try {
      const tasks = bulkDeleteTargets
        .map((slot) => {
          const slotId = String(
            slot.mongoSlotId || slot.definitionId || slot.id || "",
          ).trim();
          if (!slotId) return null;
          return deleteAdminSlot({ slotId });
        })
        .filter(Boolean);
      if (tasks.length === 0) {
        toast.info("Nothing to delete.");
        return;
      }
      await Promise.all(tasks);
      const count = tasks.length;
      const word = count === 1 ? "slot" : "slots";
      toast.success(
        scope === "duplicates"
          ? `Deleted ${count} duplicate ${word}.`
          : `Deleted ${count} ${word}.`,
      );
      setBulkDeleteOpen(false);
      setRefreshKey((value) => value + 1);
      publishCalendarRefresh({ source: "slot-bulk-delete" });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to delete slots.",
      );
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // Edit / delete / "add slot" / bulk-color / bulk-edit-all mutate slot
  // *definitions* — only wire them up in the definitions view. In "available"
  // mode the rail is inspect-only.
  const isDefinitionsMode = viewMode === "definitions";
  // RBAC: scheduling:view keeps the heatmap read-only — every mutation entry point below
  // (new/edit/duplicate/delete/bulk) wires only when the caller can also manage scheduling.
  const canMutateSlots = isDefinitionsMode && canManageScheduling;

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-background text-foreground">
      {/* Page header */}
      <header className="flex h-12 shrink-0 items-center justify-between gap-3 border-b border-border bg-card/40 px-4">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Calendar</span>
          <span className="text-muted-foreground/60">/</span>
          <span className="font-semibold text-foreground">
            {isDefinitionsMode ? "Slot definitions" : "Live availability"} · Week pattern
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="[&_svg]:size-3.5"
            onClick={() => setRefreshKey((value) => value + 1)}
          >
            <RefreshCw />
            Refresh
          </Button>
          {canManageScheduling ? (
            <Button
              type="button"
              variant="default"
              size="sm"
              className="[&_svg]:size-3.5"
              onClick={handleNewSlot}
            >
              <CalendarPlus />
              New slot
            </Button>
          ) : null}
        </div>
      </header>

      <Toolbar
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        groupBy={groupBy}
        onGroupByChange={(next) => {
          setGroupBy(next);
          // The active cell's key is from the old grouping dimension — clear
          // it so the rail doesn't try to resolve a stale entity.
          setActiveCell(null);
        }}
        rangeLabel={rangeLabel}
        weekStartDate={weekStart.toJSDate()}
        onPickDate={(date) => goToWeek(DateTime.fromJSDate(date))}
        isCurrentWeek={isCurrentWeek}
        onPrevWeek={() => goToWeek(weekStart.minus({ weeks: 1 }))}
        onNextWeek={() => goToWeek(weekStart.plus({ weeks: 1 }))}
        onToday={() => goToWeek(DateTime.now())}
        tenants={tenantOptions}
        selectedTenantIds={selectedTenantIds}
        onToggleTenant={toggleTenantFilter}
        onSelectAllTenants={selectAllTenants}
        onClearTenants={clearTenants}
        doctors={doctorOptions}
        selectedDoctorIds={selectedDoctorIds}
        onToggleDoctor={toggleDoctorFilter}
        onSelectAllDoctors={selectAllDoctors}
        onClearDoctors={clearDoctors}
      />

      {optionsError ? (
        <div className="border-b border-border bg-destructive/10 px-5 py-2 text-xs text-destructive">
          Couldn't load doctors and locations — slot names and filters may be incomplete.
        </div>
      ) : null}

      {/* Body: matrix + detail rail */}
      <div className="flex min-h-0 flex-1">
        <section className="flex min-w-0 flex-1 flex-col">
          <HeatmapMatrix
            groups={matrixGroups}
            weekDates={weekDates}
            scale={scale}
            onToggleScale={() =>
              setScaleKey((key) => (key === "work" ? "full" : "work"))
            }
            mode={viewMode}
            groupBy={groupBy}
            activeCell={activeCell}
            onSelectCell={setActiveCell}
            isLoading={isLoading}
            hideSunday={hideSunday}
          />

          {/* Footer / legend */}
          <div className="flex shrink-0 items-center justify-between gap-4 border-t border-border bg-muted/30 px-5 py-2 text-[11px] text-muted-foreground">
            <span>
              Click a cell to inspect that {groupBy === "tenant" ? "location" : "doctor"} × weekday
            </span>
            {/* <div className="flex min-w-0 items-center gap-3 overflow-hidden">
              <span className="shrink-0 text-muted-foreground/60">Color = location (if not set for slot)</span>
              {tenantOptions.map((tenant) => (
                <span key={tenant.id} className="inline-flex shrink-0 items-center gap-1.5">
                  <span
                    className="inline-block h-2.5 w-3 rounded-[2px]"
                    style={{ background: `oklch(0.62 0.16 ${hashHue(tenant.id)})` }}
                  />
                  <span className="text-foreground/85">{tenant.name}</span>
                </span>
              ))}
            </div> */}
          </div>
        </section>

        {activeGroup ? (
          <DetailRail
            entity={activeGroup}
            entityType={groupBy}
            weekday={activeCell.weekday}
            slots={activeSlots}
            selectedSlotIds={selectedSlotIds}
            onToggleSlotSelected={
              canMutateSlots ? toggleSlotSelected : undefined
            }
            onSetAllSelected={
              canMutateSlots ? setAllSlotsSelected : undefined
            }
            onSelectSession={
              canMutateSlots ? setSlotsSelectedBySession : undefined
            }
            slotSelectionKey={slotSelectionKey}
            onAddSlot={canMutateSlots ? handleAddSlotToDay : undefined}
            onEditSlot={canMutateSlots ? handleEditSlot : undefined}
            onDuplicateSlot={canMutateSlots ? handleDuplicateSlot : undefined}
            onDeleteSlot={canMutateSlots ? setDeleteTarget : undefined}
            onApplyColor={canMutateSlots ? handleApplyColor : undefined}
            colorBusy={isApplyingColor}
            onBulkEdit={
              canMutateSlots ? () => setBulkEditOpen(true) : undefined
            }
            bulkEditBusy={isBulkEditing}
            onBulkDuplicate={
              canMutateSlots ? () => setBulkDuplicateOpen(true) : undefined
            }
            bulkDuplicateBusy={isBulkDuplicating}
            onBulkDelete={
              canMutateSlots
                ? () => {
                    // Reset to the "all" default on each opening — keeps the
                    // legacy click path safe even when duplicates exist.
                    setBulkDeleteScope("all");
                    setBulkDeleteOpen(true);
                  }
                : undefined
            }
            bulkDeleteBusy={isBulkDeleting}
            recentlyEditedIds={recentlyEditedSlotIds}
            recentlyEditedFields={recentlyEditedFields}
          />
        ) : null}
      </div>

      <SlotFormSheet
        open={slotForm.open}
        onOpenChange={(next) => setSlotForm((current) => ({ ...current, open: next }))}
        mode={slotForm.mode}
        slot={slotForm.slot}
        selectedDate={slotForm.selectedDate}
        defaultDoctorId={slotForm.defaultDoctorId}
        defaultTenantId={slotForm.defaultTenantId}
        doctorOptions={doctorOptions}
        tenantOptions={tenantOptions}
        onSaved={() => setRefreshKey((value) => value + 1)}
      />

      <BulkEditSheet
        open={bulkEditOpen}
        onOpenChange={(next) => {
          if (!next && isBulkEditing) return; // don't close mid-submit
          setBulkEditOpen(next);
        }}
        // Pass only the selected subset so the sheet's Mixed/Shared detection
        // reflects what will actually be edited.
        slots={selectedSlots}
        busy={isBulkEditing}
        onApply={handleBulkEdit}
      />

      <BulkEditSheet
        mode="duplicate"
        open={bulkDuplicateOpen}
        onOpenChange={(next) => {
          if (!next && isBulkDuplicating) return; // don't close mid-submit
          setBulkDuplicateOpen(next);
        }}
        // Same selection-scoping as the edit sheet — only the chosen rows
        // get cloned.
        slots={selectedSlots}
        busy={isBulkDuplicating}
        onApply={handleBulkDuplicate}
        tenantOptions={tenantOptions.map((opt) => ({
          value: opt.id,
          label: opt.name,
        }))}
        doctorOptions={doctorOptions.map((opt) => ({
          value: opt.id,
          label: opt.name,
        }))}
      />

      <Dialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open && !isDeleting) setDeleteTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="size-5 text-amber-500" aria-hidden />
              Delete slot
            </DialogTitle>
            <DialogDescription>
              {deleteTarget?.slotType === "recurring"
                ? "This soft-deletes the recurring definition — every occurrence disappears from availability."
                : "This soft-deletes the slot definition and removes it from availability."}
            </DialogDescription>
          </DialogHeader>
          {deleteTarget ? (
            <div className="rounded-md border border-border/60 bg-muted/30 p-3 text-sm">
              <p className="font-medium text-foreground">
                {deleteTarget.title || deleteTarget.placeholder || "Slot"}
              </p>
              <p className="mt-1 text-xs tabular-nums text-muted-foreground">
                {formatRange(deleteTarget.startAt, deleteTarget.endAt)}
                {deleteTarget.doctor ? ` · ${deleteTarget.doctor}` : ""}
              </p>
            </div>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDelete}
              disabled={isDeleting}
              aria-busy={isDeleting}
            >
              {isDeleting ? <Spinner className="mr-2 size-4" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={bulkDeleteOpen}
        onOpenChange={(open) => {
          if (!open && isBulkDeleting) return; // don't close mid-submit
          setBulkDeleteOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="size-5 text-amber-500" aria-hidden />
              Delete slots
            </DialogTitle>
            <DialogDescription>
              Soft-deletes the chosen slot definitions — recurring ones
              disappear from availability entirely.
            </DialogDescription>
          </DialogHeader>
          {/* Scope picker — lets the user trim to just the redundant copies
              instead of wiping the whole group. Counts on each row tell them
              exactly how many records they're about to remove. */}
          <fieldset className="space-y-2">
            <legend className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              What to delete
            </legend>
            <label
              className={cn(
                "flex cursor-pointer items-start gap-2 rounded-md border p-3 text-sm transition-colors",
                bulkDeleteScope === "all"
                  ? "border-destructive/50 bg-destructive/5"
                  : "border-border hover:border-foreground/20",
              )}
            >
              <input
                type="radio"
                name="bulk-delete-scope"
                value="all"
                checked={bulkDeleteScope === "all"}
                onChange={() => setBulkDeleteScope("all")}
                disabled={isBulkDeleting}
                className="mt-0.5 accent-destructive"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-foreground">
                    All selected slots
                  </span>
                  <span className="font-mono text-xs tabular-nums text-foreground">
                    {selectedSlots.length}
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Removes every slot you have checked in this group.
                </p>
              </div>
            </label>
            <label
              className={cn(
                "flex items-start gap-2 rounded-md border p-3 text-sm transition-colors",
                duplicateCount === 0
                  ? "cursor-not-allowed border-border bg-muted/30 opacity-60"
                  : bulkDeleteScope === "duplicates"
                    ? "cursor-pointer border-destructive/50 bg-destructive/5"
                    : "cursor-pointer border-border hover:border-foreground/20",
              )}
            >
              <input
                type="radio"
                name="bulk-delete-scope"
                value="duplicates"
                checked={bulkDeleteScope === "duplicates"}
                onChange={() => setBulkDeleteScope("duplicates")}
                disabled={isBulkDeleting || duplicateCount === 0}
                className="mt-0.5 accent-destructive"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-foreground">
                    Only duplicates
                  </span>
                  <span className="font-mono text-xs tabular-nums text-foreground">
                    {duplicateCount}
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {duplicateCount === 0
                    ? "No duplicate slots in this group."
                    : `Keeps 1 of each unique slot; removes the ${duplicateCount} extra ${duplicateCount === 1 ? "copy" : "copies"}.`}
                </p>
              </div>
            </label>
          </fieldset>
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
            <p className="font-medium text-foreground">
              {bulkDeleteCount}{" "}
              {bulkDeleteCount === 1 ? "record" : "records"} will be deleted.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              This cannot be undone from the UI.
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setBulkDeleteOpen(false)}
              disabled={isBulkDeleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={isBulkDeleting || bulkDeleteCount === 0}
              aria-busy={isBulkDeleting}
            >
              {isBulkDeleting ? <Spinner className="mr-2 size-4" /> : null}
              Delete {bulkDeleteCount}{" "}
              {bulkDeleteCount === 1 ? "slot" : "slots"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SlotsHeatmapPage;
